/**
 * Tipos primitivos compartidos y el principio que evita el peor fallo de Cuentia.
 *
 * PRINCIPIO RECTOR (mejora A18 del plan): **el contrato es uno solo**. El
 * backend, la web y las aplicaciones móviles importan estos mismos esquemas.
 *
 * Y todos los objetos son **estrictos**: si llega un campo que no está
 * declarado, la petición se rechaza con un error claro en lugar de ignorarlo.
 *
 * ¿Por qué? En Cuentia el frontend enviaba `categoria_id` y el backend leía
 * `category_id`. Como el backend descartaba en silencio lo que no reconocía, el
 * usuario veía «Guardado» mientras su categoría se perdía para siempre. Fallar
 * ruidosamente es infinitamente mejor que perder datos con una sonrisa.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
//  Importes
// ---------------------------------------------------------------------------
/** Máximo representable con seguridad en JavaScript (2^53 − 1). */
export const MAX_PESOS = 9_007_199_254_740_991;
/**
 * Importe en pesos colombianos. Siempre un entero: el peso no usa centavos en
 * la práctica y así se eliminan los errores de redondeo de los decimales.
 */
export const zPesos = z
    .number({ invalid_type_error: 'El importe debe ser un número' })
    .int('El importe debe ser un número entero de pesos, sin decimales')
    .safe('El importe es demasiado grande')
    .min(-MAX_PESOS)
    .max(MAX_PESOS);
/** Importe positivo (montos de movimientos, cuotas, presupuestos). */
export const zPositivePesos = zPesos.positive('El importe debe ser mayor que cero');
/** Importe cero o positivo (saldos iniciales, aportes, abonos). */
export const zNonNegativePesos = zPesos.nonnegative('El importe no puede ser negativo');
// ---------------------------------------------------------------------------
//  Identificadores y fechas
// ---------------------------------------------------------------------------
export const zUuid = z.string().uuid('Identificador inválido');
export const zId = zUuid;
/** Fecha local `YYYY-MM-DD` (hora de Colombia). */
export const zDateISO = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe tener el formato AAAA-MM-DD');
/** Mes `YYYY-MM` (hora de Colombia). */
export const zMonthKey = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'El mes debe tener el formato AAAA-MM');
/** Hora `HH:mm`. */
export const zTimeHHmm = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'La hora debe tener el formato HH:mm');
/** Instante en ISO 8601 con zona horaria. */
export const zInstantISO = z
    .string()
    .datetime({ offset: true, message: 'La fecha y hora deben estar en formato ISO 8601 con zona' });
/** Fecha y hora tal como las envía el usuario: día y hora locales de Colombia. */
export const zLocalDateTime = z
    .object({
    date: zDateISO,
    time: zTimeHHmm.default('12:00'),
})
    .strict();
/** Moneda ISO de 3 letras. */
export const zCurrency = z
    .string()
    .regex(/^[A-Z]{3}$/, 'La moneda debe ser un código ISO de tres letras');
// ---------------------------------------------------------------------------
//  Texto y validaciones de uso común
// ---------------------------------------------------------------------------
export const zNonEmptyText = (max, label = 'texto') => z
    .string()
    .trim()
    .min(1, `El ${label} no puede estar vacío`)
    .max(max, `El ${label} no puede superar ${max} caracteres`);
export const zOptionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal('').transform(() => undefined));
export const zEmail = z
    .string()
    .trim()
    .toLowerCase()
    .email('El correo electrónico no es válido')
    .max(255);
export const zColor = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'El color debe tener el formato #RRGGBB');
// ---------------------------------------------------------------------------
//  Paginación y sobres
// ---------------------------------------------------------------------------
export const zPagination = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/**
 * Booleano que llega por la barra de direcciones.
 *
 * NO se usa `z.coerce.boolean()` porque convierte cualquier cadena no vacía en
 * `true`: `?includeDeleted=false` daría `true`. Es exactamente la clase de
 * trampa silenciosa que este proyecto quiere eliminar.
 */
export const zQueryBoolean = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((value) => value === true || value === 'true' || value === '1');
/** Forma única de todos los errores de la API. */
export const zApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        /** Detalle por campo, cuando el fallo es de validación. */
        fields: z
            .array(z.object({ path: z.string(), message: z.string() }))
            .optional(),
    }),
});
/** Códigos de error estables: la interfaz decide con ellos, no con el texto. */
export const ApiErrorCode = {
    VALIDATION: 'VALIDATION_ERROR',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
    INTERNAL: 'INTERNAL_ERROR',
};
// ---------------------------------------------------------------------------
//  Utilidad para definir objetos estrictos sin repetir `.strict()`
// ---------------------------------------------------------------------------
/**
 * Crea un objeto Zod **estricto**: cualquier propiedad no declarada provoca un
 * error de validación en lugar de ignorarse.
 */
export function strictObject(shape) {
    return z.object(shape).strict();
}
//# sourceMappingURL=common.js.map